import { usePwaContext } from "../hooks/use-pwa-context.js";
import { usePwaFlags } from "../hooks/use-pwa-flags.js";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Body1, Box, Button, Cover, Flex, Heading1, IconIosShare, Modal, Stack, TextButton, useNavBar } from "@myphx-engineering/phx-design-system";
import { useHotkeys } from "@myphx-engineering/phx-design-system/hooks";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/components/IOSAddToHomeOverlay.tsx
const IOSAddToHomeOverlay = ({ dismiss, visible }) => {
	const { setBackOverrideState } = useNavBar();
	const location = useLocation();
	const { appName, pwaId, pwaAssets } = usePwaContext();
	const [showModal, setShowModal] = useState(false);
	const { t } = useTranslation("pwa");
	const { flags, setFlag } = usePwaFlags(pwaId);
	const closeModal = useCallback(() => {
		setFlag("addToHome", true);
		setShowModal(false);
		dismiss();
	}, [
		dismiss,
		setFlag,
		setShowModal
	]);
	const closeTip = useCallback(() => {
		if (!visible) return;
		if (flags.addToHome) dismiss();
		else setShowModal(true);
	}, [
		dismiss,
		flags.addToHome,
		setShowModal,
		visible
	]);
	useHotkeys([["Escape", closeTip]]);
	useEffect(() => {
		if (!visible) return;
		setBackOverrideState({ overrideFn: closeTip });
		return () => setBackOverrideState({ overrideFn: null });
	}, [
		closeTip,
		setBackOverrideState,
		visible
	]);
	useEffect(() => closeTip(), [closeTip, location.key]);
	return /* @__PURE__ */ jsxs(Cover, {
		visible,
		children: [/* @__PURE__ */ jsxs(Stack, {
			align: "center",
			gap: "lg",
			p: "sm",
			w: "100%",
			children: [
				/* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx("img", {
					src: pwaAssets.illustrations?.addToHome,
					alt: t("ios.addToHome.illustrations.addToHome"),
					width: "200",
					height: "200"
				}) }),
				/* @__PURE__ */ jsxs(Stack, {
					gap: "sm",
					children: [/* @__PURE__ */ jsx(Heading1, {
						ta: "center",
						children: t("ios.addToHome.heading", { appName })
					}), /* @__PURE__ */ jsx(Body1, {
						ta: "center",
						children: /* @__PURE__ */ jsx(Trans, {
							i18nKey: "ios.addToHome.instructions",
							defaults: "Add an icon to your home screen by tapping on the share button <icon /> in Safari's toolbar, scroll down, and select \"Add to Home Screen\".",
							components: { icon: /* @__PURE__ */ jsx(IconIosShare, {}) }
						})
					})]
				}),
				/* @__PURE__ */ jsxs(Flex, {
					gap: "xxxs",
					children: [/* @__PURE__ */ jsx(Body1, {
						inline: true,
						children: t("ios.addToHome.alreadyDone")
					}), /* @__PURE__ */ jsx(TextButton, {
						onClick: closeTip,
						"data-testid": "dismiss-tip-button",
						children: t("ios.addToHome.dismiss")
					})]
				})
			]
		}), /* @__PURE__ */ jsx(Modal, {
			opened: showModal,
			onClose: closeModal,
			title: t("ios.addToHome.modal.title"),
			children: /* @__PURE__ */ jsxs(Stack, {
				gap: "xl",
				children: [
					/* @__PURE__ */ jsx(Body1, { children: t("ios.addToHome.modal.content", { appName }) }),
					/* @__PURE__ */ jsx(Flex, {
						align: "center",
						justify: "center",
						children: /* @__PURE__ */ jsx("img", {
							src: pwaAssets.illustrations?.addToHomeFromMenu,
							alt: t("ios.addToHome.illustrations.addFromMenu"),
							width: "340",
							height: "180"
						})
					}),
					/* @__PURE__ */ jsx(Button, {
						onClick: closeModal,
						children: t("ios.addToHome.modal.done")
					})
				]
			})
		})]
	});
};

//#endregion
export { IOSAddToHomeOverlay };